import { gql } from "@apollo/client";

// ─── Fragments ────────────────────────────────────────────────────────────────

export const STORED_INTERVAL_FRAGMENT = gql`
  fragment StoredIntervalFields on StoredInterval {
    unit
    value
    days
    isPreset
    presetId
  }
`;

export const SUBSCRIPTION_ORDER_FRAGMENT = gql`
  fragment SubscriptionOrderFields on SubscriptionOrder {
    id
    status
    channel
    duration
    pricePerDelivery
    dispenserUmlagePerDelivery
    dispenserTotalCost
    shopifySubscriptionContractId
    funnelSessionId
    draftOrderId
    startDate
    endDate
    nextRenewalDate
    lastOrderDate
    shopifyOrderIds
    notes
    createdAt
    updatedAt
    customerId
    shopifyCustomerId
    customer {
      id
      displayName
      email
    }
    contractVersion
    contractMonths
    dispenserMonthlyFee
    dispenserContractId
    intervalChangeUsed
    intervalChangeDeadline
    earlyTerminationFeePreview
    storedInterval {
      ...StoredIntervalFields
    }
    plan {
      id
      name
      channel
    }
  }
  ${STORED_INTERVAL_FRAGMENT}
`;

export const SUBSCRIPTION_PLAN_FRAGMENT = gql`
  fragment SubscriptionPlanFields on SubscriptionPlan {
    id
    name
    description
    channel
    isActive
    availableDurations
    availableIntervalSlugs
    allowCustomIntervals
    baseSubscriptionDiscountPercent
    durationBonus1yrPercent
    durationBonus2yrPercent
    dispenserSurcharge1yrPercent
    dispenserSurcharge2yrPercent
    cancellationFeeType
    cancellationFeeValue
    createdAt
    updatedAt
  }
`;

// ─── Subscription queries ────────────────────────────────────────────────────

export const GET_SUBSCRIPTION_ORDERS = gql`
  query GetSubscriptionOrders(
    $status: SubscriptionStatus
    $customerId: String
    $channel: ChannelEnum
    $contractVersion: String
  ) {
    subscriptionOrders(
      status: $status
      customerId: $customerId
      channel: $channel
      contractVersion: $contractVersion
    ) {
      ...SubscriptionOrderFields
    }
  }
  ${SUBSCRIPTION_ORDER_FRAGMENT}
`;

export const GET_SUBSCRIPTION_ORDER = gql`
  query GetSubscriptionOrder($id: ID!) {
    subscriptionOrder(id: $id) {
      ...SubscriptionOrderFields
    }
    subscriptionAuditLog(subscriptionOrderId: $id) {
      id
      subjectType
      subjectId
      actor { type id name }
      action
      before
      after
      reason
      createdAt
    }
  }
  ${SUBSCRIPTION_ORDER_FRAGMENT}
`;

export const CALCULATE_CANCELLATION_FEE = gql`
  query CalculateCancellationFee($id: ID!) {
    calculateCancellationFee(subscriptionOrderId: $id) {
      subscriptionOrderId
      remainingMonths
      feePercent
      feeAmount
      description
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($id: ID!) {
    cancelSubscription(id: $id) {
      subscriptionOrderId
      feeAmount
      description
    }
  }
`;

// ─── Phase 10.3.2 · Admin-Customer-Service-Mutations ────────────────────────

export const ADMIN_CREATE_MAKEUP_DELIVERY = gql`
  mutation AdminCreateMakeupDelivery($input: AdminMakeupDeliveryInput!) {
    adminCreateMakeupDelivery(input: $input) {
      success
      message
      subscriptionId
    }
  }
`;

export const ADMIN_ADJUST_CANCELLATION_FEE = gql`
  mutation AdminAdjustCancellationFee($subscriptionId: ID!, $newFee: Float!, $reason: String!) {
    adminAdjustCancellationFee(
      subscriptionId: $subscriptionId
      newFee: $newFee
      reason: $reason
    ) {
      success
      message
      subscriptionId
    }
  }
`;

export const ADMIN_UPDATE_SHIPPING_ADDRESS = gql`
  mutation AdminUpdateShippingAddress(
    $subscriptionId: ID!
    $address: AdminAddressInput!
    $reason: String!
  ) {
    adminUpdateShippingAddress(
      subscriptionId: $subscriptionId
      address: $address
      reason: $reason
    ) {
      success
      message
      subscriptionId
    }
  }
`;

export const ADMIN_MERGE_CUSTOMER_SUBSCRIPTIONS = gql`
  mutation AdminMergeCustomerSubscriptions(
    $sourceCustomerId: String!
    $targetCustomerId: String!
    $reason: String!
  ) {
    adminMergeCustomerSubscriptions(
      sourceCustomerId: $sourceCustomerId
      targetCustomerId: $targetCustomerId
      reason: $reason
    ) {
      success
      message
    }
  }
`;

export const BULK_UPDATE_SUBSCRIPTIONS = gql`
  mutation BulkUpdateSubscriptions($input: BulkUpdateSubscriptionsInput!, $dryRun: Boolean) {
    bulkUpdateSubscriptions(input: $input, dryRun: $dryRun) {
      matched
      applied
      skippedContractLocked
      errors
      dryRun
    }
  }
`;

export const PAUSE_SUBSCRIPTION = gql`
  mutation PauseSubscription($id: ID!) {
    pauseSubscription(id: $id) {
      id
      status
    }
  }
`;

export const RESUME_SUBSCRIPTION = gql`
  mutation ResumeSubscription($id: ID!) {
    resumeSubscription(id: $id) {
      id
      status
    }
  }
`;

// ─── Plan queries ─────────────────────────────────────────────────────────────

export const GET_SUBSCRIPTION_PLANS = gql`
  query GetSubscriptionPlans($channel: ChannelEnum) {
    subscriptionPlans(activeOnly: false, channel: $channel) {
      ...SubscriptionPlanFields
    }
  }
  ${SUBSCRIPTION_PLAN_FRAGMENT}
`;

export const GET_SUBSCRIPTION_PLAN = gql`
  query GetSubscriptionPlan($id: ID!) {
    subscriptionPlan(id: $id) {
      ...SubscriptionPlanFields
    }
  }
  ${SUBSCRIPTION_PLAN_FRAGMENT}
`;

export const CREATE_SUBSCRIPTION_PLAN = gql`
  mutation CreateSubscriptionPlan($input: CreateSubscriptionPlanInput!) {
    createSubscriptionPlan(input: $input) {
      ...SubscriptionPlanFields
    }
  }
  ${SUBSCRIPTION_PLAN_FRAGMENT}
`;

export const UPDATE_SUBSCRIPTION_PLAN = gql`
  mutation UpdateSubscriptionPlan($id: ID!, $input: UpdateSubscriptionPlanInput!) {
    updateSubscriptionPlan(id: $id, input: $input) {
      ...SubscriptionPlanFields
    }
  }
  ${SUBSCRIPTION_PLAN_FRAGMENT}
`;

export const DELETE_SUBSCRIPTION_PLAN = gql`
  mutation DeleteSubscriptionPlan($id: ID!) {
    deleteSubscriptionPlan(id: $id) {
      hardDeleted
      reason
      referencedOrderCount
    }
  }
`;

// ─── Shopify product tag queries (live pass-through) ─────────────────────────

export const SHOPIFY_PRODUCT_ROW_FRAGMENT = gql`
  fragment ShopifyProductRowFields on ShopifyProductRow {
    id
    title
    handle
    tags
    satinoCode
    productType
    productLine
    firstVariantSku
  }
`;

export const GET_SHOPIFY_PRODUCTS = gql`
  query GetShopifyProducts($first: Int, $after: String, $search: String) {
    shopifyProducts(first: $first, after: $after, search: $search) {
      products {
        ...ShopifyProductRowFields
      }
      hasNextPage
      endCursor
    }
  }
  ${SHOPIFY_PRODUCT_ROW_FRAGMENT}
`;

export const GET_SHOPIFY_PRODUCTS_ALL = gql`
  query GetShopifyProductsAll($search: String) {
    shopifyProductsAll(search: $search) {
      products {
        ...ShopifyProductRowFields
      }
      truncated
    }
  }
  ${SHOPIFY_PRODUCT_ROW_FRAGMENT}
`;

export const GET_REQUIRED_TAG_REFERENCE = gql`
  query GetRequiredTagReference {
    requiredTagReference {
      group
      tag
      label
      appliesTo
    }
  }
`;

export const GET_TAG_TAXONOMY_VALUES = gql`
  query GetTagTaxonomyValues {
    tagTaxonomyValues {
      groupKey
      tag
      label
      labelI18n {
        lang
        value
      }
      icon
      appliesTo
      sortOrder
    }
  }
`;

export const UPSERT_TAG_TAXONOMY_VALUE = gql`
  mutation UpsertTagTaxonomyValue($input: TagTaxonomyValueInput!) {
    upsertTagTaxonomyValue(input: $input) {
      groupKey
      tag
      label
      labelI18n {
        lang
        value
      }
      icon
      appliesTo
      sortOrder
    }
  }
`;

export const DELETE_TAG_TAXONOMY_VALUE = gql`
  mutation DeleteTagTaxonomyValue($groupKey: String!, $tag: String!) {
    deleteTagTaxonomyValue(groupKey: $groupKey, tag: $tag)
  }
`;

export const REFRESH_FUNNEL_CATALOG = gql`
  mutation RefreshFunnelCatalog {
    refreshFunnelCatalog {
      totalDispensers
      totalRefills
      invalidatedAt
    }
  }
`;

export const EVALUATE_BULK_FILTER = gql`
  query EvaluateBulkFilter($filter: BulkFilterInput!) {
    evaluateBulkFilter(filter: $filter) {
      products {
        ...ShopifyProductRowFields
      }
      truncated
      totalMatched
    }
  }
  ${SHOPIFY_PRODUCT_ROW_FRAGMENT}
`;

export const BULK_UPDATE_PRODUCTS = gql`
  mutation BulkUpdateProducts($updates: [ProductBulkUpdateInput!]!, $dryRun: Boolean!) {
    bulkUpdateProducts(updates: $updates, dryRun: $dryRun) {
      dryRun
      processed
      succeeded
      failed
      items {
        productId
        title
        beforeTags
        afterTags
        metafieldChanges {
          namespace
          key
          before
          after
        }
        error
      }
    }
  }
`;

export const GET_SPAREPARTS_INVENTORY = gql`
  query GetSparepartsInventory {
    sparepartsInventory {
      fetchedAt
      fromCache
      totalCount
      issuesByKind {
        kind
        count
      }
      items {
        id
        title
        handle
        sku
        price
        tags
        compatibleDispenserSkus
        partTypes
        color
        featuredImage
        health
      }
    }
  }
`;

// ─── Category-Config CRUD (Funnel-Kategorien) ────────────────────────────────

export const CATEGORY_CONFIG_FRAGMENT = gql`
  fragment CategoryConfigFields on CategoryConfig {
    id
    key
    label
    labelEn
    labelI18n {
      lang
      value
    }
    shopifyCategoryTag
    iconName
    imageUrl
    subQuizSteps {
      questionKey
      skipIf { questionKey optionKey }
    }
    sortOrder
    isActive
  }
`;

export const GET_CATEGORY_CONFIGS = gql`
  query GetCategoryConfigs($activeOnly: Boolean) {
    categoryConfigs(activeOnly: $activeOnly) {
      ...CategoryConfigFields
    }
  }
  ${CATEGORY_CONFIG_FRAGMENT}
`;

export const UPSERT_CATEGORY_CONFIG = gql`
  mutation UpsertCategoryConfig($id: ID, $input: CategoryConfigInput!) {
    upsertCategoryConfig(id: $id, input: $input) {
      ...CategoryConfigFields
    }
  }
  ${CATEGORY_CONFIG_FRAGMENT}
`;

export const DELETE_CATEGORY_CONFIG = gql`
  mutation DeleteCategoryConfig($id: ID!) {
    deleteCategoryConfig(id: $id)
  }
`;

export const PRUNE_LEGACY_FUNNEL_DATA = gql`
  mutation PruneLegacyFunnelData($dryRun: Boolean!) {
    pruneLegacyFunnelData(dryRun: $dryRun) {
      quizOptionsByStepKey { stepKey count }
      quizOptionsTotalCount
      copyFieldsCleared
      dryRun
    }
  }
`;

export const RECONCILE_FUNNEL_V2_ORDER = gql`
  mutation ReconcileFunnelV2Order($subscriptionOrderId: ID!) {
    reconcileFunnelV2Order(subscriptionOrderId: $subscriptionOrderId) {
      ok
      reason
      subscriptionOrderId
    }
  }
`;

export const CLEANUP_HARDWARE_GHOST_ORDERS = gql`
  mutation CleanupHardwareGhostOrders($dryRun: Boolean!) {
    cleanupHardwareGhostOrders(dryRun: $dryRun) {
      ghostsFound
      ghostsDeleted
      dryRun
    }
  }
`;

export const EXPORT_SHOP_CONFIG = gql`
  query ExportShopConfig {
    exportShopConfig
  }
`;

export const IMPORT_SHOP_CONFIG = gql`
  mutation ImportShopConfig($bundleJson: String!, $dryRun: Boolean!) {
    importShopConfig(bundleJson: $bundleJson, dryRun: $dryRun) {
      dryRun
      metaobjectsWritten
      funnelQuizOptionsUpserted
      categoryConfigsUpserted
      subscriptionPlansUpserted
      tagTaxonomyValuesUpserted
      productLinesUpserted
      dispensersUpserted
      dispenserProductOptionsUpserted
      warnings
    }
  }
`;

export const GET_FUNNEL_DATA_HEALTH = gql`
  query GetFunnelDataHealth {
    funnelDataHealth {
      summary {
        totalDispensers
        totalRefills
        dispensersWithoutRefillsCount
        refillsWithoutLineCount
        refillsWithoutLineTagCount
        refillsWithoutCompatibleCodesCount
        soapRefillsWithoutProductTagCount
        dispensersWithMissingColorVariantCount
      }
      dispensersWithoutRefills {
        id
        title
        handle
        category
        codingCodes
        missingColors
      }
      refillsWithoutLineTag {
        id
        title
        handle
        compatibleCodes
        tags
        lineSlugs
      }
      refillsWithoutCompatibleCodes {
        id
        title
        handle
        compatibleCodes
        tags
        lineSlugs
      }
      soapRefillsWithoutProductTag {
        id
        title
        handle
        compatibleCodes
        tags
        lineSlugs
      }
      dispensersWithMissingColorVariant {
        id
        title
        handle
        category
        codingCodes
        missingColors
      }
    }
  }
`;

export const GET_CURRENT_SHOP_DOMAIN = gql`
  query GetCurrentShopDomain {
    currentShopDomain
  }
`;

/** Lightweight summary-only-Variante für Dashboard-Banner + Nav-Badge. */
export const GET_FUNNEL_DATA_HEALTH_SUMMARY = gql`
  query GetFunnelDataHealthSummary {
    funnelDataHealth {
      summary {
        totalDispensers
        totalRefills
        dispensersWithoutRefillsCount
        refillsWithoutLineCount
        refillsWithoutLineTagCount
        refillsWithoutCompatibleCodesCount
        soapRefillsWithoutProductTagCount
        dispensersWithMissingColorVariantCount
      }
    }
  }
`;

export const UPDATE_SHOPIFY_PRODUCT_TAGS = gql`
  mutation UpdateShopifyProductTags($productId: ID!, $tags: [String!]!) {
    updateShopifyProductTags(productId: $productId, tags: $tags) {
      ...ShopifyProductRowFields
    }
  }
  ${SHOPIFY_PRODUCT_ROW_FRAGMENT}
`;

export const GET_SHOPIFY_PRODUCTS_BY_TAG = gql`
  query GetShopifyProductsByTag($tag: String!, $first: Int, $after: String) {
    shopifyProductsByTag(tag: $tag, first: $first, after: $after) {
      products {
        ...ShopifyProductRowFields
      }
      hasNextPage
      endCursor
    }
  }
  ${SHOPIFY_PRODUCT_ROW_FRAGMENT}
`;

export const BULK_ASSIGN_PRODUCT_TAG = gql`
  mutation BulkAssignProductTag($tag: String!, $productIds: [ID!]!, $mode: TagAssignMode!) {
    bulkAssignProductTag(tag: $tag, productIds: $productIds, mode: $mode) {
      updatedCount
      errors
    }
  }
`;

export const GET_SHOPIFY_PRODUCT_METAFIELDS = gql`
  query GetShopifyProductSatinoMetafields($productId: ID!) {
    shopifyProductSatinoMetafields(productId: $productId) {
      key
      value
      type
    }
  }
`;

export const SET_SHOPIFY_PRODUCT_METAFIELD = gql`
  mutation SetShopifyProductSatinoMetafield(
    $productId: ID!
    $key: String!
    $value: String!
    $type: String
  ) {
    setShopifyProductSatinoMetafield(
      productId: $productId
      key: $key
      value: $value
      type: $type
    ) {
      key
      value
      type
    }
  }
`;

// ─── Shopify Files-Library (Bild-Picker) ─────────────────────────────────────

export const GET_SHOPIFY_IMAGE_FILES = gql`
  query GetShopifyImageFiles($first: Int, $after: String, $search: String) {
    shopifyImageFiles(first: $first, after: $after, search: $search) {
      files {
        id
        alt
        url
        width
        height
        createdAt
        fileName
      }
      hasNextPage
      endCursor
    }
  }
`;

export const DELETE_PRODUCT_LINE = gql`
  mutation DeleteProductLine($id: ID!) {
    deleteProductLine(id: $id) {
      hardDeleted
      reason
      referencedOrderCount
    }
  }
`;

// ─── Runtime-configurable copy + plan defaults ───────────────────────────────

export const FUNNEL_COPY_FIELDS = gql`
  fragment FunnelCopyFields on FunnelCopy {
    primaryColor
    headerLogoUrl
    intro {
      header
      body
      cta
      bannerImageUrl
      priceTeaser
      steps { title body }
    }
    steps {
      frequency { header body }
      sensor { header body }
      capacity { header body }
      extras { header body }
      refillLine { header body }
      duration { header body }
      interval { header body }
    }
    preview { header body ctaPrimary ctaSecondary solutionLabel perCardChangeLabel }
    exitIntent { header body cta }
    stepperLabels { q1 q2 q3 q4 q5 rooms preview shop }
    stepperLabelsV2 { context needs subquiz quantities paper matching bundle }
    loadingMessage
    validationHint
    emptyIntervalsHint
    checkoutErrorMessage
    cartTitle
    checkoutButtonLabel
    socialProof { customerCount rating co2Saving }
    dispenserColorImages { whiteImageUrl blackImageUrl }
    matching {
      eyebrow
      title
      sub
      badgeLabel
      rankingTitle
      topMatchLabel
      submitLabel
      noRecommendation
      noRecommendationHint
      lineStatsJson
    }
  }
`;

export const PDP_WIDGET_COPY_FIELDS = gql`
  fragment PdpWidgetCopyFields on PdpWidgetCopy {
    modeOnetimeLabel
    modeSubscribeLabel
    sectionIntervalLabel
    customIntervalLabel
    customIntervalHint
    priceLabel
    savingsLabelPrefix
    ctaAddToCartLabel
    ctaLoadingLabel
    errorMessage
    stockInStockLabel
    taxShippingNote
    outOfStockLabel
    intervalPlaceholder
    customIntervalOptionLabel
    planUnavailableNote
    customIntervalDialogTitle
    customIntervalCancelLabel
    customIntervalSubmitLabel
  }
`;

export const PLAN_DEFAULTS_FIELDS = gql`
  fragment PlanDefaultsFields on PlanDefaults {
    channel
    baseDiscountPercent
    durationBonus1yrPercent
    durationBonus2yrPercent
    dispenserSurcharge1yrPercent
    dispenserSurcharge2yrPercent
    cancellationFeeType
    cancellationFeeValue
    defaultIntervalSlugs
    allowCustomIntervals
    hardwareSurchargeFactor
    hardwareFinanceMonths
    refillDiscountFactor
    monthlySurchargeFactor
    monthlySurchargeEnabled
    contractOptions {
      months
      label
      surchargePercent
      refillDiscountPercent
      isDefault
      sortOrder
    }
  }
`;

export const GET_FUNNEL_COPY = gql`
  query GetFunnelCopy {
    funnelCopy { ...FunnelCopyFields }
  }
  ${FUNNEL_COPY_FIELDS}
`;

export const GET_PDP_WIDGET_COPY = gql`
  query GetPdpWidgetCopy {
    pdpWidgetCopy { ...PdpWidgetCopyFields }
  }
  ${PDP_WIDGET_COPY_FIELDS}
`;

export const GET_PLAN_DEFAULTS = gql`
  query GetPlanDefaults {
    planDefaults { ...PlanDefaultsFields }
  }
  ${PLAN_DEFAULTS_FIELDS}
`;

export const UPDATE_FUNNEL_COPY = gql`
  mutation UpdateFunnelCopy($input: FunnelCopyInput!) {
    updateFunnelCopy(input: $input) { ...FunnelCopyFields }
  }
  ${FUNNEL_COPY_FIELDS}
`;

export const GET_FUNNEL_COPY_OVERRIDES = gql`
  query GetFunnelCopyOverrides {
    funnelCopyOverrides {
      lang
      json
      filledPaths
    }
  }
`;

export const UPDATE_FUNNEL_COPY_OVERRIDE = gql`
  mutation UpdateFunnelCopyOverride($lang: String!, $json: String!) {
    updateFunnelCopyOverride(lang: $lang, json: $json) {
      lang
      json
      filledPaths
    }
  }
`;

export const UPDATE_PDP_WIDGET_COPY = gql`
  mutation UpdatePdpWidgetCopy($input: PdpWidgetCopyInput!) {
    updatePdpWidgetCopy(input: $input) { ...PdpWidgetCopyFields }
  }
  ${PDP_WIDGET_COPY_FIELDS}
`;

export const GET_PDP_WIDGET_COPY_OVERRIDES = gql`
  query GetPdpWidgetCopyOverrides {
    pdpWidgetCopyOverrides {
      lang
      json
      filledPaths
    }
  }
`;

export const UPDATE_PDP_WIDGET_COPY_OVERRIDE = gql`
  mutation UpdatePdpWidgetCopyOverride($lang: String!, $json: String!) {
    updatePdpWidgetCopyOverride(lang: $lang, json: $json) {
      lang
      json
      filledPaths
    }
  }
`;

export const UPDATE_PLAN_DEFAULTS = gql`
  mutation UpdatePlanDefaults($input: PlanDefaultsInput!) {
    updatePlanDefaults(input: $input) { ...PlanDefaultsFields }
  }
  ${PLAN_DEFAULTS_FIELDS}
`;

// ─── Product queries ──────────────────────────────────────────────────────────

export const GET_PRODUCT_LINES = gql`
  query GetProductLines($activeOnly: Boolean) {
    productLines(activeOnly: $activeOnly) {
      id
      slug
      name
      description
      certifications
      colorHex
      isActive
      sortOrder
      shopifyTag
      quizLabel
      iconName
      logoUrl
      packImageUrl
      quizWeights {
        optionKey
        weight
      }
      matchingExplanation
      usps {
        value
        label
      }
    }
  }
`;

export const CREATE_PRODUCT_LINE = gql`
  mutation CreateProductLine($input: CreateProductLineInput!) {
    createProductLine(input: $input) {
      id
      slug
      name
      isActive
    }
  }
`;

export const UPDATE_PRODUCT_LINE = gql`
  mutation UpdateProductLine($id: ID!, $input: CreateProductLineInput!) {
    updateProductLine(id: $id, input: $input) {
      id
      slug
      name
      description
      certifications
      colorHex
      isActive
      sortOrder
      shopifyTag
      quizLabel
      iconName
      logoUrl
      packImageUrl
      quizWeights {
        optionKey
        weight
      }
      matchingExplanation
      usps {
        value
        label
      }
    }
  }
`;

export const GET_DISPENSERS = gql`
  query GetDispensers($activeOnly: Boolean) {
    dispensers(activeOnly: $activeOnly) {
      id
      code
      name
      description
      imageUrl
      categories
      shopifyProductId
      isActive
      productOptions {
        id
        productLineSlug
        shopifyProductId
        name
        isDefault
        sortOrder
        isActive
      }
    }
  }
`;

export const CREATE_DISPENSER = gql`
  mutation CreateDispenser($input: CreateDispenserInput!) {
    createDispenser(input: $input) {
      id
      code
      name
    }
  }
`;

export const UPDATE_DISPENSER = gql`
  mutation UpdateDispenser($id: ID!, $input: CreateDispenserInput!) {
    updateDispenser(id: $id, input: $input) {
      id
      code
      name
      categories
      isActive
    }
  }
`;

export const UPSERT_DISPENSER_PRODUCT_OPTION = gql`
  mutation UpsertDispenserProductOption($input: UpsertDispenserProductOptionInput!) {
    upsertDispenserProductOption(input: $input) {
      id
      dispenserCode
      productLineSlug
      name
      isDefault
      isActive
    }
  }
`;

// ─── Reporting queries ────────────────────────────────────────────────────────

export const GET_SUBSCRIPTION_METRICS = gql`
  query GetSubscriptionMetrics($from: DateTime!, $to: DateTime!, $productLineSlug: String) {
    subscriptionMetrics(from: $from, to: $to, productLineSlug: $productLineSlug) {
      totalActive
      totalNew
      totalCancelled
      totalRevenue
      churnRate
      mrr
      arr
      hardwareResidualValue
      directPurchaseCount
      directPurchaseRevenue
      byStatus
      byProductLine
    }
  }
`;

export const GET_HARDWARE_AMORTISATION_REPORT = gql`
  query GetHardwareAmortisationReport {
    hardwareAmortisationReport {
      totalActive
      totalResidualValue
      buckets {
        label
        count
        residualValue
      }
    }
  }
`;

export const GET_EARLY_TERMINATION_REPORT = gql`
  query GetEarlyTerminationReport($from: DateTime!, $to: DateTime!) {
    earlyTerminationReport(from: $from, to: $to) {
      from
      to
      totalFee
      count
      entries {
        subscriptionOrderId
        shopifyCustomerId
        cancelledAt
        monthsPaid
        monthsRemaining
        dispenserMonthlyFee
        fee
      }
    }
  }
`;

export const GET_MRR_SPLIT = gql`
  query GetMrrSplit {
    mrrSplit {
      hardware
      refill
      legacyV1
      total
    }
  }
`;

export const GET_MONTHLY_KPI_MATRIX = gql`
  query GetMonthlyKpiMatrix($from: DateTime!, $to: DateTime!) {
    monthlyKpiMatrix(from: $from, to: $to) {
      from
      to
      years {
        year
        totalNew
        totalCancelled
        totalRevenue
        totalMrrDelta
        cells {
          year
          month
          newSubs
          cancelled
          revenue
          mrrDelta
        }
      }
    }
  }
`;

export const GET_REVENUE_METRICS = gql`
  query GetRevenueMetrics($from: DateTime!, $to: DateTime!) {
    revenueMetrics(from: $from, to: $to) {
      totalRevenue
      averageRevenuePerCustomer
      revenueByProductLine
      revenueByMonth {
        year
        month
        revenue
        orderCount
        newSubscriptions
        cancelledSubscriptions
      }
    }
  }
`;

// ─── Funnel Quiz Options (admin-editable) ─────────────────────────────────────

export const FUNNEL_QUIZ_OPTION_FRAGMENT = gql`
  fragment FunnelQuizOptionFields on FunnelQuizOption {
    id
    stepKey
    optionKey
    label
    labelI18n { lang value }
    description
    descriptionI18n { lang value }
    iconName
    imageUrl
    shopifyTag
    isMust
    appliesTo
    parentCategoryKey
    filterTags
    filterCodes
    skipIfQuestionKey
    skipIfOptionKey
    intervalMonths
    allowReducedRefills
    sortOrder
    isActive
  }
`;

export const GET_FUNNEL_QUIZ_OPTIONS = gql`
  query GetFunnelQuizOptions($stepKey: String) {
    funnelQuizOptions(stepKey: $stepKey) {
      ...FunnelQuizOptionFields
    }
  }
  ${FUNNEL_QUIZ_OPTION_FRAGMENT}
`;

export const UPSERT_FUNNEL_QUIZ_OPTION = gql`
  mutation UpsertFunnelQuizOption($id: ID, $input: FunnelQuizOptionInput!) {
    upsertFunnelQuizOption(id: $id, input: $input) {
      ...FunnelQuizOptionFields
    }
  }
  ${FUNNEL_QUIZ_OPTION_FRAGMENT}
`;

export const DELETE_FUNNEL_QUIZ_OPTION = gql`
  mutation DeleteFunnelQuizOption($id: ID!) {
    deleteFunnelQuizOption(id: $id)
  }
`;

// ─── Funnel v2 — Intervall-Wechsel ────────────────────────────────────────────

export const CHANGE_SUBSCRIPTION_INTERVAL = gql`
  mutation ChangeSubscriptionInterval(
    $id: ID!
    $newIntervalSlug: String!
    $isAdminOverride: Boolean
  ) {
    changeSubscriptionInterval(
      id: $id
      newIntervalSlug: $newIntervalSlug
      isAdminOverride: $isAdminOverride
    ) {
      ...SubscriptionOrderFields
    }
  }
  ${SUBSCRIPTION_ORDER_FRAGMENT}
`;
